import { useMemo } from 'react';
import { TableHeaderTypes } from '../../Atoms/Table/Types';
import { AvailableColumn } from '../types';

interface UseTableColumnConfigurationProps {
  showCheckbox: boolean;
  selectedTagColumns: string[];
  availableColumns: AvailableColumn[];
}

/**
 * Custom hook for generating table column configuration
 */
const useTableColumnConfiguration = ({
  showCheckbox,
  selectedTagColumns,
  availableColumns,
}: UseTableColumnConfigurationProps) => {
  const tableColumns: TableHeaderTypes[] = useMemo(() => {
    // Define static columns configuration inside useMemo
    const STATIC_COLUMNS = [
      {
        id: -1,
        headerTitle: '#',
        relatedKey: 'index',
        defaultValue: '',
        width: '48px',
        ariaLabel: 'Row index',
        style: {
          flexGrow: 0,
          flexShrink: 0,
          width: '48px',
          minWidth: '48px',
          maxWidth: '48px',
          textAlign: 'center',
          justifyContent: 'center',
          alignItems: 'center',
        },
      },
      {
        id: 0,
        headerTitle: '',
        relatedKey: 'checkbox',
        defaultValue: '',
        width: '40px',
        isOptional: true,
        textAlign: 'center',
        ariaLabel: 'Select item',
        style: {
          flexGrow: 0,
          flexShrink: 0,
          width: '40px',
          minWidth: '40px',
          maxWidth: '40px',
          textAlign: 'center',
          justifyContent: 'center',
          alignItems: 'center',
        },
      },
      {
        id: 1,
        headerTitle: '',
        relatedKey: 'preview',
        defaultValue: '',
        width: '100px',
        ariaLabel: 'Question preview',
        style: {
          flexGrow: 0,
          flexShrink: 0,
          width: '100px',
          minWidth: '100px',
          maxWidth: '100px',
        },
      },
      {
        id: 2,
        headerTitle: 'Item',
        relatedKey: 'item',
        defaultValue: '',
        width: '40%',
        minWidth: '300px',
        ariaLabel: 'Item content',
        style: {
          flexGrow: 1, // Allow this column to grow
          flexShrink: 1,
          minWidth: '300px',
        },
      },
      // {
      //   id: 3,
      //   headerTitle: 'Date',
      //   relatedKey: 'date',
      //   defaultValue: '',
      //   width: '100px',
      //   ariaLabel: 'Creation date',
      //   style: {
      //     flexGrow: 0,
      //     flexShrink: 0,
      //     width: '100px',
      //     minWidth: '100px',
      //     maxWidth: '100px'
      //   }
      // },
    ];

    // Only include checkbox column if showCheckbox is true
    const filteredColumns = STATIC_COLUMNS.filter(
      (col) => !col.isOptional || (col.relatedKey === 'checkbox' && showCheckbox),
    );

    // Create the dynamic tag columns
    const tagColumns = selectedTagColumns
      .filter((columnKey) => availableColumns.some((col) => col.key === columnKey)) // Only include columns that exist in availableColumns
      .map((columnKey, index) => {
        // Double width for Skill Display and Skill columns
        const isSkillColumn = columnKey === 'Skill Display' || columnKey === 'Skill';
        const columnWidth = isSkillColumn ? '240px' : '100px';

        // Custom display names for table headers
        let headerTitle =
          availableColumns.find((col) => col.key === columnKey)?.label || columnKey;
        if (columnKey === 'Blooms Taxonomy') {
          headerTitle = 'Blooms';
        }
        if (columnKey === 'Depth Of Knowledge') {
          headerTitle = 'DOK';
        }

        return {
          id: STATIC_COLUMNS.length + index,
          headerTitle,
          relatedKey: columnKey.toLowerCase(),
          defaultValue: '',
          width: columnWidth,
          ariaLabel: `${columnKey} filter`,
          style: {
            flexGrow: 0,
            flexShrink: 0,
            width: columnWidth,
            minWidth: columnWidth,
            maxWidth: columnWidth,
            whiteSpace: 'normal',
            overflow: 'visible',
            textOverflow: 'unset',
            textAlign: 'center',
            justifyContent: 'center',
            alignItems: 'center',
          },
        };
      });

    // All Tags column (will be added at the end)
    const ALL_TAGS_COLUMN = {
      id: 999, // High ID to ensure it appears last
      headerTitle: 'All Tags',
      relatedKey: 'tags',
      defaultValue: '',
      width: '120px',
      ariaLabel: 'View all tags',
      style: {
        flexGrow: 0,
        flexShrink: 0,
        width: '120px',
        minWidth: '120px',
        maxWidth: '120px',
        textAlign: 'center',
        justifyContent: 'center',
        alignItems: 'center',
      },
    };

    const finalColumns = [...filteredColumns, ...tagColumns, ALL_TAGS_COLUMN];

    return finalColumns;
  }, [showCheckbox, selectedTagColumns, availableColumns]);

  return tableColumns;
};

export default useTableColumnConfiguration;
